import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import * as GC from '@grapecity/spread-sheets';
import { IEmpyreanUser } from '@models/empyrean-user';
import {
  AccountCharacteristicConflict,
  AccountCharacteristicConflictNumber,
  AccountCharacteristicConflictRequest
} from '@models/import/account-characteristic';
import { WindowRef } from '@progress/kendo-angular-dialog';
import { PageChangeEvent } from '@progress/kendo-angular-pager';
import { ImportConflictsService } from '@services/import/import-conflicts.service';
import { MenuService } from '@services/menu.service';
import { UserService } from '@services/user.service';
import { hostStyle, spreadBackColor } from '@shared/spread/spread-settings';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { onWindowSizeChanged, populatePagedConflicts, setUpWorkbook } from './import-conflicts.spread';

const PAGE_SIZE = 1000;

@Component({
  selector: 'app-import-conflicts',
  templateUrl: './import-conflicts.component.html',
  styleUrls: ['./import-conflicts.component.scss']
})
export class ImportConflictsComponent implements OnInit, AfterViewInit, OnDestroy {
  public pagination = {
    total: 0,
    skip: 0,
    pageSize: PAGE_SIZE,
    pageSizes: [PAGE_SIZE, PAGE_SIZE * 5, PAGE_SIZE * 10]
  };

  public conflicts: AccountCharacteristicConflict[] = [];
  public hostStyle = hostStyle;
  public isLoading = true;
  public isUpdating = false;
  public showOnlyActiveConflicts = true;
  public showPagination = false;
  public spreadBackColor = spreadBackColor;

  public numberActiveConflicts = 0;
  private numberConflicts = 0;
  private sheet: GC.Spread.Sheets.Worksheet;
  private spread: GC.Spread.Sheets.Workbook;
  private timeOut: NodeJS.Timeout = null;
  private users: IEmpyreanUser[];
  private readonly subscriptions: Subscription[] = [];

  get isDepositsAdmin(): boolean {
    return this.menu.isDepositsAdmin();
  }

  constructor(
    public windowRef: WindowRef,
    private readonly conflictService: ImportConflictsService,
    private readonly menu: MenuService,
    private readonly userService: UserService
  ) {}

  ngOnInit() {}

  ngAfterViewInit() {
    this.subscriptions.push(
      this.windowRef.window.instance.heightChange.pipe(debounceTime(100)).subscribe(height => this.onSizeChanged()),
      this.windowRef.window.instance.widthChange.pipe(debounceTime(100)).subscribe(width => this.onSizeChanged(width)),
      this.windowRef.window.instance.stateChange.pipe(debounceTime(100)).subscribe(state => this.onSizeChanged())
    );
    this.onSizeChanged();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subs => subs.unsubscribe());
    this.conflictService.clearCache();
  }

  public workbookInit(args) {
    this.spread = args.spread;
    this.sheet = this.spread.getActiveSheet();
    setUpWorkbook(this.spread, this.sheet);
    this.loadData();
  }

  public async loadData() {
    this.isLoading = true;
    const request: AccountCharacteristicConflictRequest = {
      PageNumber: 1 + this.pagination.skip / this.pagination.pageSize,
      PageSize: this.pagination.pageSize,
      OnlyActive: this.showOnlyActiveConflicts
    };

    let conflicts: AccountCharacteristicConflictNumber;
    [conflicts, this.conflicts, this.users] = await Promise.all([
      this.conflictService.GetNumberOfConflicts().toPromise(),
      this.conflictService.GetConflicts(request),
      this.userService.getUsers().toPromise()
    ]);

    this.numberConflicts = conflicts.NumberConflicts;
    this.numberActiveConflicts = conflicts.NumberActiveConflicts;
    this.pagination.total = this.showOnlyActiveConflicts ? this.numberActiveConflicts : this.numberConflicts;
    this.showPagination = this.conflicts.length < this.pagination.total;
    this.pageData(this.conflicts);
  }

  public async updateConflicts() {
    Swal.fire({
      title: 'Update Account Characteristics',
      html: `<p>Updating Account Characteristics will trigger a product reaggregation based on the updated mapping.<p>
        <br />
        <p>Continue?<p>`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel'
    }).then(async swalResponse => {
      if (swalResponse.value) {
        // Continue
        this.isUpdating = true;
        const result = await this.conflictService.updateConflicts().toPromise();
        this.isUpdating = false;
        this.windowRef.close(result);
      }
    });
  }

  public onSizeChanged(width: number = null): void {
    this.menu.emitResize();

    if (width !== null) {
      onWindowSizeChanged(this.sheet, width);
    }
  }

  public close() {
    this.windowRef.close();
  }

  public async onPageChange(e: PageChangeEvent): Promise<void> {
    this.pagination.skip = e.skip;
    this.pagination.pageSize = e.take;
    this.GetNewPage();
  }

  private pageData(conflicts: AccountCharacteristicConflict[]): void {
    this.isLoading = true;
    populatePagedConflicts(this.sheet, conflicts, this.showOnlyActiveConflicts, this.users);
    this.onSizeChanged();
    this.isLoading = false;
  }

  async onShowOnlyActiveConflictsChanged() {
    this.pagination.total = this.showOnlyActiveConflicts ? this.numberActiveConflicts : this.numberConflicts;

    if (this.pagination.skip > this.pagination.total) {
      // pages to go back, if there are not enough after going from 'Show All' to 'Show Active'
      const pages = Math.ceil((this.pagination.skip - this.pagination.total) / this.pagination.pageSize);
      this.pagination.skip -= pages * this.pagination.pageSize;
    }

    this.GetNewPage();
  }

  private async GetNewPage() {
    this.isLoading = true;
    const request: AccountCharacteristicConflictRequest = {
      PageNumber: 1 + this.pagination.skip / this.pagination.pageSize,
      PageSize: this.pagination.pageSize,
      OnlyActive: this.showOnlyActiveConflicts
    };
    this.conflicts = await this.conflictService.GetConflicts(request);
    this.pageData(this.conflicts);
  }

  private debounceFunction(func, wait, immediate = false) {
    const later = () => {
      this.timeOut = null;
      if (!immediate) {
        func();
      }
    };

    clearTimeout(this.timeOut);
    this.timeOut = setTimeout(later, wait);
    if (immediate && !this.timeOut) {
      func();
    }
  }
}
